import { useEffect, useMemo, useRef, useState } from "react";
import "./AllReportGroup.scss";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest";
import { ScaleLoader } from "react-spinners";
import { FiSearch } from "react-icons/fi";
import { Home } from "lucide-react";
import { MdDelete, MdEdit, MdAdd, MdClose } from "react-icons/md";
import { motion } from "framer-motion";
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton";
import { ToggleSwitch } from "../../../components/toogle/ToggleSwitch";
import { showToast } from "../../../toastService";
import Select from "react-select";
import { qilloIconMap, qilloIconOptions } from "../../../utils/iconMapping";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { generateUniqueCode, logData } from "../../../utils/genericFunctions";
import { RiListSettingsLine } from "react-icons/ri";
import { CustomRadioGroup } from "../../../components/radio/CustomRadioGroup";


const AllReportGroup = () => {
    const { currentTheme } = useSelector((state) => state.theme);
    const { currentUser } = useSelector((state) => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const { t } = useTranslation();
    const [isProcessing, setIsProcessing] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState("None");
    const [confirmationActiontext, setConfirmationActiontext] = useState("");
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [searchTerm, setSearchTerm] = useState("");

    const [currentAction, setCurrentAction] = useState(null);
    const [isGroupDialogOpened, setIsGroupDialogOpened] = useState(false);
    const [isReportDialogOpened, setIsReportDialogOpened] = useState(false);
    const [searchTermReport, setSearchTermReport] = useState("");

    const [valueSets, setValueSets] = useState([]);
    const [reportCategories, setReportCategories] = useState([]);
    const [reportKeys, setReportKeys] = useState([]);

    const [reportsGroups, setReportsGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);

    const [definedReports, setDefinedReports] = useState([]);
    const [selectedReport, setSelectedReport] = useState(null);

    const [repGroup, setRepGroup] = useState(null);
    const [repGroupName, setRepGroupName] = useState(null);
    const [repEnabled, setRepEnabled] = useState(null);
    const [selectedIcon, setSelectedIcon] = useState(null);
    const reportNameRef = useRef(null);
    const groupNameRef = useRef(null);
    const [allowedFormats, setAllowedFormats] = useState([
        {
            value: "PDF",
            label: "PDF",
        },
        {
            value: "EXCEL",
            label: "EXCEL",
        },
        {
            value: "CSV",
            label: "CSV",
        },
        {
            value: "JSON",
            label: "JSON",
        },
        {
            value: "SCREEN",
            label: "SCREEN",
        },
        {
            value: "NONE",
            label: "NONE",
        }
    ]);


    // parameters
    const [isParamDialogOpened, setIsParamDialogOpened] = useState(false);
    const [selectedParamIcon, setSelectedParamIcon] = useState(null);
    const [reportParameters, setReportParameters] = useState([]);
    const [selectedParameter, setSelectedParameter] = useState(null);
    const [schemas, setSchemas] = useState([]);
    const defaultTypes = ["NONE", "QUERY", "FIXED_VALUE", "PARAMETER"];

    const fetchSchemas = async () => {
        try {
            const res = await newRequest.get("/valueSet/listSchemas");
            const options = res.data.map((codes) => ({
                value: codes.name,
                label: codes.name,
            }));

            setSchemas(options);
        } catch (error) {
            console.error("Error fetching Occupations :", error);
        }
    };

    const fetchValueSets = async () => {
        try {
            const res = await newRequest.get("/reportGroup/getValueSets");
            const options = res.data.map((row) => ({
                vs_validation_type: row.vs_validation_type,
                vs_source: row.vs_source,
                value: row.vs_code,
                label: row.vs_name,
            }));

            setValueSets(options);
        } catch (error) {
            console.error("Error fetching Occupations :", error);
        }
    };


    const fetchReportGroups = async () => {
        try {
            setIsLoading(true);
            const res = await newRequest.post("/reportGroup/findAll", { result_limit: 10 });
            setReportsGroups(res.data);
            if (res.data) {
                setSelectedGroup(res.data[0]);
                setRepGroup(res.data[0]?.rep_group);
                setRepGroupName(res.data[0]?.rep_group_name);
                setRepEnabled(res.data[0]?.rep_enabled);
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const fetchReportCategories = async () => {
        try {
            const res = await newRequest.post("/sysCode/findAll", { sys_type: "Report Category", });
            const options = res.data.map((val) => ({
                value: val?.sys_code,
                label: val?.sys_name,
            }));
            setReportCategories(options);
        } catch (error) {
            console.error("Error fetching jurisdictions :", error);
        }
    };

    const fetchReportKeys = async () => {
        try {
            const res = await newRequest.post("/sysCode/findAll", { sys_type: "Report Keys", });
            const options = res.data.map((val) => ({
                value: val?.sys_code,
                label: val?.sys_name,
            }));
            setReportKeys(options);
        } catch (error) {
            console.error("Error fetching jurisdictions :", error);
        }
    };


    const fetchDefinedReports = async (reportGroup) => {
        try {
            setIsProcessing(true);
            const res = await newRequest.post("/reportGroup/getReports", { rd_rep_group: reportGroup });

            // logData("setDefinedReports", res.data);

            setDefinedReports(res.data);
            if (res.data) {
                setSelectedReport({ ...res.data[0], flag: "edit" });
            }
            setIsProcessing(false);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const handleReportChange = (e, rowId, field) => {
        try {
            let value = null;

            if (e && e.target) {
                value = e.target.value;
            } else if (e && typeof e === "object" && "value" in e) {
                value = e.value;
            } else {
                value = e;
            }

            console.log(`field: ${field}, value:`, value);

            // Always update selected report first
            setSelectedReport((prev) => ({
                ...prev,
                [field]: value,
                flag: prev?.flag || "new",
            }));

            // Update array only if record already exists there
            setDefinedReports((prev) => {
                const newData = [...prev];
                const index = newData.findIndex((row) => row._id === rowId);

                if (index !== -1) {
                    newData[index] = {
                        ...newData[index],
                        [field]: value,
                        flag: newData[index]?.flag === "new" ? "new" : "edit",
                    };
                }

                return newData;
            });
        } catch (error) {
            console.log(error);
        }
    };

    const handleParameterChange = (e, rowId, field) => {
        try {
            let value = null;

            if (e && e.target) {
                value = e.target.value;
            } else if (e && typeof e === "object" && "value" in e) {
                value = e.value;
            } else {
                value = e;
            }

            console.log(`field: ${field}, value:`, value);

            // Always update selected report first
            setSelectedParameter((prev) => ({
                ...prev,
                [field]: value,
                flag: prev?.flag || "new",
            }));

            // Update array only if record already exists there
            setReportParameters((prev) => {
                const newData = [...prev];
                const index = newData.findIndex((row) => row._id === rowId);

                if (index !== -1) {
                    newData[index] = {
                        ...newData[index],
                        [field]: value,
                        flag: newData[index]?.flag === "new" ? "new" : "edit",
                    };
                }

                return newData;
            });
        } catch (error) {
            console.log(error);
        }
    };

    const formatParamName = (value = "", sep = "_") => {
        return value
            .trim()
            .replace(/\s+/g, sep)           // replace spaces/tabs with separator
            .replace(/[^a-zA-Z0-9_-]/g, ""); // allow letters, numbers, underscore, and hyphen
    };

    const handleSearchGroups = async () => {
        try {
            setIsProcessing(true);
            if (searchTerm) {
                const res = await newRequest.post("/reportGroup/search", { search_input: searchTerm });
                setReportsGroups(res.data);
                setSelectedGroup(res.data[0]);
                setRepGroup(res.data[0]?.rep_group);
                setRepGroupName(res.data[0]?.rep_group_name);
                setRepEnabled(res.data[0]?.rep_enabled);
            } else {
                fetchReportGroups();
            }
        } catch (error) {
            console.error("Error searching data:", error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleGroupSubmit = async () => {
        try {
            if (currentAction === "editGroup") {
                const res = await newRequest.put(`/reportGroup/upd/${selectedGroup?._id}`,
                    {
                        rep_group: repGroup,
                        rep_group_name: repGroupName,
                        rep_enabled: repEnabled
                    });

                if (res.status === 200) {
                    const updatedReport = res.data?.doc;
                    setReportsGroups((prevData) => {
                        return prevData.map((report) =>
                            report._id === updatedReport._id ? updatedReport : report
                        );
                    });

                    setIsGroupDialogOpened(false);
                    showToast(t("updated"), "success");
                } else {
                    setIsGroupDialogOpened(false);
                    showToast(res.data, "error");
                }
            } else if (currentAction === "addGroup") {
                const res = await newRequest.post("/reportGroup/add",
                    {
                        rep_group: repGroup,
                        rep_group_name: repGroupName,
                        rep_enabled: "Y",
                        rep_reports: []
                    });

                logData("report", res.data);

                if (res.data?.msg === "added") {
                    const addedGroup = res.data?.doc;
                    setReportsGroups((prevReports) => [...prevReports, addedGroup]);
                    setSelectedGroup(addedGroup);
                    setIsGroupDialogOpened(false);
                    showToast(t('added'), "success");
                } else if (res.data.msg === "exists") {
                    showToast(`Report group ${repGroup} already exists`, "error");
                } else {
                    showToast("error adding report group!", "error");
                }
            }
        } catch (error) {
            console.error("Error searching data:", error);
        }
    };

    const handleReportSubmit = async () => {
        try {

            if (["editReport", "addReport"]) {
                if (!selectedReport?.rd_name) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify name!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
                if (!selectedReport?.rd_rep_key) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify report key!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
                if (!selectedReport?.rd_file_name) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify report file!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
                if (!selectedReport?.rd_endpoint) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify endpoint!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
            }

            if (currentAction === "editReport") {

                const res = await newRequest.put(`/reportGroup/updateReport/${selectedReport?._id}`,
                    {
                        rd_name: selectedReport?.rd_name,
                        rd_rep_key: selectedReport?.rd_rep_key,
                        rd_ref: selectedReport?.rd_ref,
                        rd_icon: selectedIcon?.value,
                        rd_description: selectedReport?.rd_description,
                        rd_category: selectedReport?.rd_category,
                        rd_type: selectedReport?.rd_type,
                        rd_output_type: selectedReport?.rd_output_type,
                        rd_file_path: selectedReport?.rd_file_path,
                        rd_file_name: selectedReport?.rd_file_name,
                        rd_procedure_name: selectedReport?.rd_procedure_name,
                        rd_endpoint: selectedReport?.rd_endpoint,
                        rd_allow_background: selectedReport?.rd_allow_background,
                        rd_change_format: selectedReport?.rd_change_format,
                        rd_enabled: selectedReport?.rd_enabled
                    });

                console.log(`updateReport ${JSON.stringify(res.data, null, 2)}`);

                if (res.data?.msg === "updated") {
                    const updatedReport = res.data?.doc;
                    // console.log(`updatedReport ${JSON.stringify(updatedReport, null, 2)}`);
                    setDefinedReports((prevReports) => {
                        return prevReports.map((report) =>
                            report?._id === updatedReport?._id ? updatedReport : report
                        );
                    });
                    setIsReportDialogOpened(false);
                    showToast("Updated!", "success");
                } else {
                    showToast("error updating report!", "error");
                }
            } else if (currentAction === "addReport") {
                const res = await newRequest.post("/reportGroup/addReport",
                    {
                        rd_rep_group: selectedGroup?.rep_group,
                        rd_name: selectedReport?.rd_name,
                        rd_rep_key: selectedReport?.rd_rep_key,
                        rd_ref: selectedReport?.rd_ref,
                        rd_icon: selectedIcon?.value,
                        rd_description: selectedReport?.rd_description,
                        rd_category: selectedReport?.rd_category || "General",
                        rd_type: selectedReport?.rd_type,
                        rd_output_type: selectedReport?.rd_output_type,
                        rd_file_path: "./assets/c$orgCode$/reportTemplates",
                        rd_file_name: selectedReport?.rd_file_name,
                        rd_procedure_name: selectedReport?.rd_procedure_name,
                        rd_endpoint: selectedReport?.rd_endpoint || "agenda-reps",
                        rd_allow_background: selectedReport?.rd_allow_background,
                        rd_change_format: selectedReport?.rd_change_format,
                        rd_enabled: selectedReport?.rd_enabled
                    });
                if (res.data?.msg === "added") {
                    const addedReport = res.data?.doc;
                    setDefinedReports((prevReports) => [...prevReports, addedReport]);
                    setSelectedReport(addedReport);
                    setIsReportDialogOpened(false);
                    showToast(t("added"), "success");
                } else {
                    showToast("Error adding report!", "error");
                }
            }
        } catch (error) {
            console.error("Error searching data:", error);
        }
    };

    const handleParameterSubmit = async () => {
        try {

            if (["editParameter", "addParameter"]) {
                if (!selectedParameter?.param_name) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify name!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
                if (!selectedParameter?.param_label) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify label!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
                if (!selectedParameter?.param_value_set_code) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify value set!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
                if (!selectedParameter?.param_help_text) {
                    setNotifyDialogTitle(t("note"));
                    setNotifyDialogText(`Please specify help text!`);
                    setIsNotifyDialogOpen(true);
                    return;
                }
            }

            if (currentAction === "addParameter") {
                const res = await newRequest.post("/reportGroup/addReportParameter",
                    {
                        report_docid: selectedReport?._id,
                        param_name: selectedParameter?.param_name,
                        param_label: selectedParameter?.param_label,
                        param_value_set_code: selectedParameter?.param_value_set_code,
                        param_required: selectedParameter?.param_required,
                        param_default_type: selectedParameter?.param_default_type,
                        param_default_value: selectedParameter?.param_default_value,
                        param_placeholder: selectedParameter?.param_placeholder || selectedParameter?.param_label,
                        param_icon: selectedParamIcon?.value,
                        param_help_text: selectedParameter?.param_help_text,
                        param_is_hidden: selectedParameter?.param_is_hidden,
                        param_is_readonly: selectedParameter?.param_is_readonly,
                        param_trigger_refresh: selectedParameter?.param_trigger_refresh,
                        param_dependent_on: [],
                    });
                if (res.data?.msg === "added") {
                    setReportParameters((prevParams) => [...prevParams, res.data?.doc]);
                    setIsParamDialogOpened(false);
                    showToast(t("added"), "success");
                } else {
                    showToast("Error adding parameter!", "error");
                }
            } else if (currentAction === "editParameter") {
                const res = await newRequest.put(`/reportGroup/updateReportParameter/${selectedReport?._id}/${selectedParameter?._id}`,
                    {
                        param_order: selectedParameter?.param_order,
                        param_name: selectedParameter?.param_name,
                        param_label: selectedParameter?.param_label,
                        param_value_set_code: selectedParameter?.param_value_set_code,
                        param_required: selectedParameter?.param_required,
                        param_default_type: selectedParameter?.param_default_type,
                        param_default_value: selectedParameter?.param_default_value,
                        param_placeholder: selectedParameter?.param_placeholder,
                        param_icon: selectedParamIcon?.value,
                        param_help_text: selectedParameter?.param_help_text,
                        param_is_hidden: selectedParameter?.param_is_hidden,
                        param_is_readonly: selectedParameter?.param_is_readonly,
                        param_trigger_refresh: selectedParameter?.param_trigger_refresh,
                        param_dependent_on: selectedParameter?.param_dependent_on,
                    });

                // console.log(`updatedParam ${JSON.stringify(res.data, null, 2)}`);

                if (res.data?.msg === "updated") {
                    const updatedParam = res.data?.doc;
                    setReportParameters((prevParams) => {
                        return prevParams.map((parameter) =>
                            parameter._id === updatedParam._id ? updatedParam : parameter
                        );
                    });
                    setIsParamDialogOpened(false);
                    showToast(t("updated"), "success");
                } else {
                    showToast("Error updating parameter!", "error");
                }
            }
        } catch (error) {
            console.error("Error searching data:", error);
        }
    };


    const handleConfirm = async () => {
        try {
            if (confirmDialogAction === "deleteReport") {
                const res = await newRequest.delete(`/reportGroup/removeReport/${selectedReport?._id}`);

                // console.log(`removeReport  ${JSON.stringify(res.data, null, 2)}`);

                if (res.data?.msg === "removed") {
                    // Remove from state without re-querying the database
                    setDefinedReports((prevReports) =>
                        prevReports.filter(group => group._id !== res.data.report_docid)
                    );
                    setSelectedReport(null);
                    showToast(t("deleted"), "success");
                } else {
                    showToast("Error deleting report!", "error");
                }
            } else if (confirmDialogAction === "deleteParameter") {
                const res = await newRequest.delete(`/reportGroup/removeReportParameter/${selectedReport?._id}/${selectedParameter?._id}`);
                if (res.data?.msg === "removed") {
                    // Remove from state without re-querying the database
                    setReportParameters((prevParams) => prevParams.filter(group => group._id !== selectedParameter?._id));
                    setSelectedParameter(null);
                    showToast(t("deleted"), "success");
                } else {
                    showToast("Error deleting parameter!", "error");
                }
            }
        } catch (err) {
            console.log(err);
        }
        // Close the dialog
        setIsConfirmDialogOpen(false);
    };

    // helper options
    const tokenOptions = [
        { value: "$$USER_ID$$", label: "$$USER_ID$$" },
        { value: "$$ORG_CODE$$", label: "$$ORG_CODE$$" },
        { value: "$$TODAY$$", label: "$$TODAY$$" },
        { value: "$$ENTITY_CODE$$", label: "$$ENTITY_CODE$$" },
        { value: "$$BRANCH_CODE$$", label: "$$BRANCH_CODE$$" },
    ];

    // helper selected option
    const selectedDefaultToken =
        tokenOptions.find(
            (opt) => opt.value === selectedParameter?.param_default_value
        ) || null;

    // helper to make react-select behave like normal input updates
    const handleParameterValueChange = (field, value) => {
        handleParameterChange(
            {
                target: {
                    name: field,
                    value,
                },
            },
            selectedParameter?._id,
            field
        );
    };

    const renderDefaultValueInput = () => {

        console.log(`selectedParameter?.param_default_type ... ${selectedParameter?.param_default_type}`);

        switch (selectedParameter?.param_default_source) {
            case "PARAMETER":
                return (
                    <div className="input-item-with-label">
                        <label
                            htmlFor="param_default_value"
                            className="form-input-label"
                            style={{ width: "100px" }}
                        >
                            {t("value").toUpperCase()}
                        </label>
                        <Select
                            inputId="param_default_value"
                            name="param_default_value"
                            menuPortalTarget={document.body}
                            styles={{
                                ...(currentTheme === "dark"
                                    ? customSelectStyles_dark
                                    : customSelectStyles_light),
                                menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                }),
                            }}
                            options={tokenOptions}
                            isSearchable={true}
                            width="350px"
                            placeholder={t("value").toUpperCase()}
                            value={selectedDefaultToken}
                            onChange={(option) =>
                                handleParameterValueChange(
                                    "param_default_value",
                                    option?.value || null
                                )
                            }
                        />
                    </div>
                );

            case "QUERY":
                return (
                    <div className="input-item-with-label">
                        <label
                            htmlFor="param_default_value"
                            className="form-input-label"
                            style={{ width: "100px" }}
                        >
                            {t("value").toUpperCase()}
                        </label>
                        <input
                            id="param_default_value"
                            name="param_default_value"
                            placeholder={t("value").toUpperCase()}
                            rows={2}
                            className="form-input-item"
                            style={{ width: "330px" }}
                            value={selectedParameter?.param_default_value || ""}
                            onChange={(e) =>
                                handleParameterChange(
                                    e,
                                    selectedParameter?._id,
                                    "param_default_value"
                                )
                            }
                        />
                    </div>
                );

            case "FIXED_VALUE":
                return (
                    <div className="input-item-with-label">
                        <label
                            htmlFor="param_default_value"
                            className="form-input-label"
                            style={{ width: "100px" }}
                        >
                            {t("value").toUpperCase()}
                        </label>
                        <input
                            id="param_default_value"
                            name="param_default_value"
                            type={
                                selectedParameter?.param_format_type === "NUMBER"
                                    ? "number"
                                    : selectedParameter?.param_format_type === "DATE"
                                        ? "date"
                                        : "text"
                            }
                            placeholder={t("value").toUpperCase()}
                            className="form-input-item"
                            style={{
                                width:
                                    selectedParameter?.param_format_type === "NUMBER"
                                        ? "200px"
                                        : "350px",
                            }}
                            value={selectedParameter?.param_default_value ?? ""}
                            onChange={(e) =>
                                handleParameterChange(
                                    e,
                                    selectedParameter?._id,
                                    "param_default_value"
                                )
                            }
                        />
                    </div>
                );

            default:
                return (
                    <div className="input-item-with-label">
                        <label
                            htmlFor="param_default_value"
                            className="form-input-label"
                            style={{ width: "100px" }}
                        >
                            {t("value").toUpperCase()}
                        </label>
                        <input
                            id="param_default_value"
                            name="param_default_value"
                            type="text"
                            placeholder={t("value").toUpperCase()}
                            className="form-input-item"
                            style={{ width: "350px" }}
                            value={selectedParameter?.param_default_value ?? ""}
                            onChange={(e) =>
                                handleParameterChange(
                                    e,
                                    selectedParameter?._id,
                                    "param_default_value"
                                )
                            }
                        />
                    </div>
                );
        }
    };

    useEffect(() => {
        if (!selectedParameter?.param_default_source) return;

        handleParameterValueChange("param_default_value", null);
    }, [selectedParameter?.param_default_source]);

    const filteredReports = useMemo(() => {
        return definedReports?.filter(
            (item) =>
                item?.rd_name?.toLowerCase().includes(searchTermReport?.toLowerCase()) ||
                item?.rd_description?.toLowerCase().includes(searchTermReport?.toLowerCase())
        );
    }, [definedReports, searchTermReport]);


    useEffect(() => {
        if (isGroupDialogOpened && groupNameRef.current) {
            groupNameRef.current.focus();
        }
    }, [isGroupDialogOpened]);

    useEffect(() => {
        if (isReportDialogOpened && reportNameRef.current) {
            reportNameRef.current.focus();
        }
    }, [isReportDialogOpened]);

    useEffect(() => {
        setDefinedReports([]);
        fetchDefinedReports(selectedGroup?.rep_group);
    }, [selectedGroup]);

    useEffect(() => {
        setReportParameters([]);
        setReportParameters(selectedReport?.rd_parameters || []);
    }, [selectedReport]);

    useEffect(() => {
        fetchReportGroups(true);
        fetchSchemas();
        fetchValueSets();
        fetchReportCategories();
        fetchReportKeys();
    }, []);

    return (
        <div className='all-report-group'>
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <motion.div
                    className="all-report-group-main-wrapper"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}>
                    <div className="all-report-group-center-wrapper">
                        <div className="center-navbar">
                            <div className="navbar-left">
                                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "3px" }}>
                                    <h1 className="page-title"> {repGroupName || t('new')} </h1>
                                    <span className="sub-title soft boldsmall">{`${repGroup} ${t('status')} : ${selectedGroup?.rep_enabled === "Y" ? t('active') : t('disabled')}`}</span>
                                </div>
                                <CircularButton
                                    currentTheme={currentTheme}
                                    onClick={() => {
                                        try {
                                            setRepGroup(selectedGroup?.rep_group);
                                            setRepGroupName(selectedGroup?.rep_group_name);
                                            setRepEnabled(selectedGroup?.rep_enabled);
                                            setCurrentAction("editGroup");
                                            setIsGroupDialogOpened(true);
                                        } catch (error) {
                                            console.log(error);
                                        }
                                    }}
                                    color="#43A047"
                                    icon={<MdEdit />}
                                    width={"30px"}
                                    height={"30px"} />
                            </div>
                            <div className="navbar-right">
                                {["admin", "superadmin"].includes(currentUser.user_type_xx) &&
                                    <CustomLoadingButton
                                        currentTheme={currentTheme}
                                        iconPosition="end"
                                        borderRadiusType="both"
                                        variant="tint"
                                        color="#26C6DA"
                                        tooltip="Add new report"
                                        endIcon={<MdAdd />}
                                        onClick={() => {
                                            try {
                                                setCurrentAction("addReport");
                                                const recordId = generateUniqueCode();
                                                setSelectedReport({
                                                    _id: recordId,
                                                    rd_rep_group: selectedGroup?.rep_group,
                                                    rd_rep_key: null,
                                                    rd_name: null,
                                                    rd_ref: null,
                                                    rd_icon: "Home",
                                                    rd_description: null,
                                                    rd_category: null,
                                                    rd_type: "Report",
                                                    rd_output_type: "PDF",
                                                    rd_file_path: null,
                                                    rd_file_name: null,
                                                    rd_procedure_name: null,
                                                    rd_endpoint: "agenda-reps",
                                                    rd_allow_background: "Y",
                                                    rd_change_format: "Y",
                                                    rd_enabled: "Y",
                                                    flag: "new"
                                                });
                                                setSelectedIcon({
                                                    value: "Home",
                                                    label: <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                        {<Home />} {"Home"}
                                                    </div>
                                                });
                                                setIsReportDialogOpened(true);
                                            } catch (error) {
                                                console.log(error);
                                            }
                                        }}
                                    >
                                        {t('addReport')}
                                    </CustomLoadingButton>}
                                {["admin", "superadmin"].includes(currentUser.user_type_xx) &&
                                    <CustomLoadingButton
                                        currentTheme={currentTheme}
                                        iconPosition="end"
                                        borderRadiusType="both"
                                        variant="tint"
                                        color="#558B2F"
                                        tooltip="Add new group"
                                        endIcon={<MdAdd />}
                                        onClick={() => {
                                            try {
                                                setCurrentAction("addGroup");
                                                setRepGroup(null);
                                                setRepGroupName(null);
                                                setRepEnabled("Y");
                                                setDefinedReports([]);
                                                setIsGroupDialogOpened(true);
                                            } catch (error) {
                                                console.log(error);
                                            }
                                        }}
                                    >
                                        {t('addGroup')}
                                    </CustomLoadingButton>}
                            </div>
                        </div>
                        <div className="center-content">
                            <div className="homeSearch" style={{ width: "40%" }}>
                                <FiSearch className="homeSearchIcon" />
                                <input
                                    className="homeSearchInput"
                                    type="text"
                                    placeholder={t('search')}
                                    value={searchTermReport}
                                    onChange={(e) => setSearchTermReport(e.target.value)}
                                />
                                {searchTermReport?.length > 0 ? (
                                    <MdClose className="homeSearchClear"
                                        onClick={() => setSearchTermReport("")}
                                    />
                                ) : (
                                    <span className="homeSearchClearPlaceholder" />
                                )}
                            </div>
                            {definedReports?.length > 0 &&
                                <div className="reports-wrapper">
                                    {filteredReports?.map((report) => {
                                        const IconComponent = qilloIconMap[report?.rd_icon] || Home;
                                        return (
                                            <div group={report?._id}
                                                className={`${selectedReport?._id === report._id ? "sub-menu-item current" : "sub-menu-item"}`}
                                                onClick={async () => setSelectedReport(report)}>
                                                <IconComponent className="sub-menu-icon" />
                                                <div className="sub-menu-content">
                                                    <div className="sub-menu-name">{report?.rd_name}</div>
                                                    <div className="sub-menu-description">{report?.rd_description}</div>
                                                </div>
                                                <div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
                                                    <CircularButton
                                                        currentTheme={currentTheme}
                                                        onClick={(e) => {
                                                            try {
                                                                e.stopPropagation();
                                                                setCurrentAction("editReport");
                                                                setSelectedReport({ ...report, flag: "edit" });
                                                                setSelectedIcon({
                                                                    value: report?.rd_icon,
                                                                    label:
                                                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                            <IconComponent className="icon" /> {report?.rd_icon}
                                                                        </div>
                                                                });
                                                                setIsReportDialogOpened(true);
                                                            } catch (error) {
                                                                console.log(error);
                                                            }
                                                        }}
                                                        color="#43A047"
                                                        icon={<MdEdit />}
                                                        width={"30px"}
                                                        height={"30px"} />
                                                    <CircularButton
                                                        currentTheme={currentTheme}
                                                        onClick={(e) => {
                                                            try {
                                                                e.stopPropagation();
                                                                setSelectedReport(report);
                                                                setConfirmationActiontext(`Do you want to delete ${report?.rd_name}?`);
                                                                setConfirmDialogAction("deleteReport");
                                                                setIsConfirmDialogOpen(true);
                                                            } catch (error) {
                                                                console.log(error);
                                                            }
                                                        }}
                                                        color="#D32F2F"
                                                        icon={<MdDelete />}
                                                        width={"30px"}
                                                        height={"30px"} />
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>}
                            <hr className="separator-h" />
                            <div className="parameters-wrapper">
                                <div className="parameters-toolbar">
                                    <h2 className="main-title">{t('parameters').toUpperCase()}</h2>
                                    {["admin", "superadmin"].includes(currentUser.user_type_xx)
                                        &&
                                        <CircularButton
                                            currentTheme={currentTheme}
                                            onClick={() => {
                                                try {
                                                    if (!selectedReport) {
                                                        setNotifyDialogTitle("Note!");
                                                        setNotifyDialogText(t('selectReport'));
                                                        setIsNotifyDialogOpen(true);
                                                        return;
                                                    } else {
                                                        setCurrentAction("addParameter");
                                                        const recordId = generateUniqueCode();
                                                        setSelectedParameter({
                                                            _id: recordId,
                                                            param_name: "",
                                                            param_label: "",
                                                            param_value_set_code: null,
                                                            param_required: "Y",
                                                            param_default_type: "NONE",
                                                            param_default_value: "",
                                                            param_placeholder: "",
                                                            param_icon: "RiListSettingsLine",
                                                            param_help_text: "",
                                                            param_is_hidden: "N",
                                                            param_is_readonly: "N",
                                                            param_trigger_refresh: "N",
                                                            param_dependent_on: [],
                                                            flag: "new"
                                                        });
                                                        setSelectedParamIcon({
                                                            value: "RiListSettingsLine",
                                                            label: <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                {<RiListSettingsLine />} {"RiListSettingsLine"}
                                                            </div>
                                                        });
                                                        setIsParamDialogOpened(true);
                                                    }
                                                } catch (error) {
                                                    console.log(error);
                                                }
                                            }}
                                            icon={<MdAdd />}
                                            width={"35px"}
                                            height={"35px"} />
                                    }
                                </div>

                                <div className="parameters-content">
                                    <div className="table-content">
                                        <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                            <Table sx={{ width: "max-content", border: 'none' }}>
                                                <TableHead className="tablehead">
                                                    <TableRow className="tablerow">
                                                        <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                            #
                                                        </TableCell>
                                                        <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                            <span>{t('prompt').toUpperCase()}</span>
                                                        </TableCell>
                                                        <TableCell className="table-cell left" style={{ maxWidth: "200px" }}>
                                                            <span>{t('group').toUpperCase()}</span>
                                                        </TableCell>
                                                        <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                            <span>{t('data').toUpperCase()}</span>
                                                        </TableCell>
                                                        {["admin", "superadmin"].includes(currentUser.user_type_xx) &&
                                                            <TableCell className="table-cell center" style={{ maxWidth: "100px" }}>
                                                                <span>{t('action').toUpperCase()}</span>
                                                            </TableCell>}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody className="tablebody">
                                                    {reportParameters?.map((row) => {
                                                        const IconComponent = qilloIconMap[row?.param_icon] || <RiListSettingsLine />;
                                                        return (
                                                            <TableRow
                                                                group={row._id}
                                                                className={`${selectedParameter?._id === row?._id ? "tablerow current" : "tablerow"}`}
                                                                onClick={() => setSelectedParameter(row)}
                                                            >
                                                                <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                                    {row?.param_order}
                                                                </TableCell>
                                                                <TableCell className="table-cell" style={{ width: "auto" }}>
                                                                    <h1 className="sub-title">{row?.param_label}</h1>
                                                                </TableCell>
                                                                <TableCell className="table-cell left" style={{ maxWidth: "200px" }}>
                                                                    <h1 className="sub-title">{row?.param_name}</h1>
                                                                </TableCell>
                                                                <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                                    <h1 className="sub-title">{row?.param_value_set_code}</h1>
                                                                </TableCell>
                                                                {["admin", "superadmin"].includes(currentUser.user_type_xx) &&
                                                                    <TableCell className="table-cell" style={{ display: 'flex', flexDirection: "row", alignItems: "center", justifyContent: 'space-around', padding: "3px", maxWidth: "100px", }}>
                                                                        <CircularButton
                                                                            currentTheme={currentTheme}
                                                                            onClick={(e) => {
                                                                                try {
                                                                                    e.stopPropagation();
                                                                                    setSelectedParameter(row);
                                                                                    setCurrentAction("editParameter");
                                                                                    setSelectedParameter({ ...row, flag: "edit" });
                                                                                    setSelectedParamIcon({
                                                                                        value: row?.param_icon,
                                                                                        label:
                                                                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                                                                <IconComponent className="icon" /> {row?.param_icon}
                                                                                            </div>
                                                                                    });
                                                                                    setIsParamDialogOpened(true);
                                                                                } catch (error) {
                                                                                    console.log(error);
                                                                                }
                                                                            }}
                                                                            color="#43A047"
                                                                            icon={<MdEdit />}
                                                                            width={"30px"}
                                                                            height={"30px"} />
                                                                        <CircularButton
                                                                            currentTheme={currentTheme}
                                                                            onClick={(e) => {
                                                                                try {
                                                                                    e.stopPropagation();
                                                                                    setSelectedParameter(row);
                                                                                    setConfirmationActiontext(`Do you want to delete ${row?.param_label}?`);
                                                                                    setConfirmDialogAction("deleteParameter");
                                                                                    setIsConfirmDialogOpen(true);
                                                                                } catch (error) {
                                                                                    console.log(error);
                                                                                }
                                                                            }}
                                                                            color="#D32F2F"
                                                                            icon={<MdDelete />}
                                                                            width={"30px"}
                                                                            height={"30px"} />
                                                                    </TableCell>}
                                                            </TableRow>)
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="all-report-group-right-wrapper">
                        <div className="homeSearch" style={{ width: "95%" }}>
                            <FiSearch className="homeSearchIcon" />
                            <input
                                className="homeSearchInput"
                                type="text"
                                placeholder={t('search')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onGroupDown={(e) => {
                                    if (e.group === 'Enter') handleSearchGroups();
                                }}
                            />
                            {searchTerm?.length > 0 && (
                                <MdClose
                                    onClick={async () => {
                                        try {
                                            await fetchReportGroups();
                                            setSearchTerm("");
                                        } catch (error) {
                                            console.log(error);
                                        }
                                    }}
                                    className="homeSearchClear"
                                />
                            )}
                        </div>
                        <div className="options-list">
                            {reportsGroups?.map((rgroup) => (
                                <>
                                    <span
                                        group={rgroup?._id}
                                        className={`${selectedGroup?._id === rgroup?._id
                                            ? "single-option active"
                                            : "single-option"
                                            }`}
                                        onClick={async () => {
                                            try {
                                                setSelectedGroup(rgroup);
                                                setRepGroup(rgroup?.rep_group);
                                                setRepGroupName(rgroup?.rep_group_name);
                                                setRepEnabled(rgroup?.rep_enabled);
                                            } catch (error) {
                                                console.log(error);
                                            }
                                        }}
                                    >
                                        <span className="cy-title">{rgroup?.rep_group_name}</span>
                                        <span className="cy-type">{rgroup?.rep_group}</span>
                                    </span>
                                </>
                            ))}
                        </div>
                    </div>
                </motion.div>
            )}
            {isGroupDialogOpened && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{`${currentAction === "addGroup" ? "ADD KEY" : "EDIT KEY"
                                }`}</h1>
                            <CircularButton
                                currentTheme={currentTheme}
                                onClick={() => setIsGroupDialogOpened(false)}
                                icon={<MdClose />}
                                width={"35px"}
                                height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="newName"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('name').toUpperCase()}
                                </label>
                                <input
                                    ref={groupNameRef}
                                    id="newName"
                                    name="newName"
                                    type="text"
                                    placeholder={t('name').toUpperCase()}
                                    value={repGroupName}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                    onChange={(e) => setRepGroupName(e.target.value)}

                                />
                            </div>
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="newGroup"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('group').toUpperCase()}
                                </label>
                                <input
                                    id="newGroup"
                                    name="newGroup"
                                    type="text"
                                    placeholder={t('group').toUpperCase()}
                                    value={repGroup}
                                    className="form-input-item"
                                    style={{ width: "250px" }}
                                    onChange={(e) => setRepGroup(e.target.value)}
                                    disabled={currentAction !== "addGroup"}
                                    onBlur={(e) => {
                                        const formatted = formatParamName(e.target.value, "-");
                                        setRepGroup(formatted);
                                    }}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label htmlFor="rep_enabled"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                />
                                <ToggleSwitch
                                    id="rep_enabled"
                                    value={repEnabled}
                                    onChange={setRepEnabled}
                                    label={t('enabled').toUpperCase()}
                                    labelWidth="max-content"
                                    disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                    border="N"
                                    style={{ width: "max-content" }}
                                />
                            </div>
                        </div>
                        <div className="overlay-actions">
                            <CustomLoadingButton
                                onClick={handleGroupSubmit}
                                borderRadiusType="both"
                                variant="tint"
                                color="#2563EB">
                                {currentAction === "addGroup" ? t('add') : t('update')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </motion.div>
            )}
            {isReportDialogOpened && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <span className="main-title">{`${currentAction === "addReport" ? t('addReport').toUpperCase() : t('editReport').toUpperCase()}`}</span>
                            <CircularButton
                                currentTheme={currentTheme}
                                onClick={() => setIsReportDialogOpened(false)}
                                icon={<MdClose />}
                                width={"35px"}
                                height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="rd_name"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('name').toUpperCase()}
                                </label>
                                <input
                                    ref={reportNameRef}
                                    id="rd_name"
                                    name="rd_name"
                                    type="text"
                                    placeholder={t('name').toUpperCase()}
                                    value={selectedReport?.rd_name}
                                    onChange={(e) => handleReportChange(e, selectedReport?._id, 'rd_name')}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                />
                            </div>

                            <div className="input-item-with-label">
                                <label htmlFor="rd_rep_key"
                                    className="form-input-label"
                                    style={{ width: "100px" }}>
                                    {t('key').toUpperCase()}
                                </label>
                                <Select
                                    id="rd_rep_key"
                                    name="rd_rep_key"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    width="350px"
                                    value={reportKeys.find(option => option.value === selectedReport?.rd_rep_key) || null}
                                    onChange={(selectedOption) => handleReportChange(selectedOption, selectedReport?._id, 'rd_rep_key')}
                                    options={reportKeys}
                                    isSearchable
                                    placeholder={t('key').toUpperCase()}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="rd_description"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('desc').toUpperCase()}
                                </label>
                                <textarea
                                    id="rd_description"
                                    name="rd_description"
                                    type="text"
                                    placeholder={t('desc').toUpperCase()}
                                    rows={2}
                                    className="form-input-item"
                                    style={{ width: "350px", height: "max-content" }}
                                    value={selectedReport?.rd_description}
                                    onChange={(e) => handleReportChange(e, selectedReport?._id, 'rd_description')}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="rd_file_name"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('file').toUpperCase()}
                                </label>
                                <input
                                    id="rd_file_name"
                                    name="rd_file_name"
                                    type="text"
                                    placeholder={t('file').toUpperCase()}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                    value={selectedReport?.rd_file_name}
                                    onChange={(e) => handleReportChange(e, selectedReport?._id, 'rd_file_name')}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="rd_endpoint"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('endpoint').toUpperCase()}
                                </label>
                                <input
                                    id="rd_endpoint"
                                    name="rd_endpoint"
                                    type="text"
                                    placeholder={t('endpoint').toUpperCase()}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                    value={selectedReport?.rd_endpoint}
                                    onChange={(e) => handleReportChange(e, selectedReport?._id, 'rd_endpoint')}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="reportFilePath"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('icon').toUpperCase()}
                                </label>
                                <Select
                                    id="icon_select"
                                    name="icon_select"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    options={qilloIconOptions}
                                    width="350px"
                                    isSearchable={true}
                                    placeholder={t('icon').toUpperCase()}
                                    value={selectedIcon}
                                    onChange={setSelectedIcon}
                                    getOptionLabel={(e) => (
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {e.icon} {e.label}
                                        </div>
                                    )}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label htmlFor="rd_category"
                                    className="form-input-label"
                                    style={{ width: "100px" }}>
                                    {t('category').toUpperCase()}
                                </label>
                                <Select
                                    id="rd_category"
                                    name="rd_category"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    width="350px"
                                    value={reportCategories.find(option => option.value === selectedReport?.rd_category) || null}
                                    onChange={(selectedOption) => handleReportChange(selectedOption, selectedReport?._id, 'rd_category')}
                                    options={reportCategories}
                                    isSearchable
                                    placeholder={t('category').toUpperCase()}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label htmlFor="report_format"
                                    className="form-input-label"
                                    style={{ width: "100px" }}>
                                    {t('format').toUpperCase()}
                                </label>
                                <Select
                                    id="report_format"
                                    name="report_format"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    width="100px"
                                    value={allowedFormats.find(option => option.value === selectedReport?.rd_output_type) || null}
                                    onChange={(selectedOption) => handleReportChange(selectedOption, selectedReport?._id, 'rd_output_type')}
                                    options={allowedFormats}
                                    isSearchable={false}
                                    placeholder={t('format').toUpperCase()}
                                />
                                <ToggleSwitch
                                    id="report_format_change"
                                    value={selectedReport?.report_format_change}
                                    onChange={(val) => handleReportChange(val, selectedReport?._id, 'report_format_change')}
                                    label={t('allowFormatChange').toUpperCase()}
                                    labelWidth="max-content"
                                    labelPosition="end"
                                    disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                    border="N"
                                    style={{ width: "max-content" }}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label htmlFor="rd_allow_background"
                                    className="form-dropdown-label"
                                    style={{ width: "90px" }}
                                />
                                <ToggleSwitch
                                    id="rd_allow_background"
                                    value={selectedReport?.rd_allow_background}
                                    onChange={(val) => handleReportChange(val, selectedReport?._id, 'rd_allow_background')}
                                    label={t('allowBackgroundRun').toUpperCase()}
                                    labelWidth="max-content"
                                    labelPosition="end"
                                    disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                    border="N"
                                    style={{ width: "max-content" }}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label htmlFor="rd_enabled"
                                    className="form-dropdown-label"
                                    style={{ width: "90px" }}
                                />
                                <ToggleSwitch
                                    id="rd_enabled"
                                    value={selectedReport?.rd_enabled}
                                    onChange={(val) => handleReportChange(val, selectedReport?._id, 'rd_enabled')}
                                    label={t('enabled').toUpperCase()}
                                    labelWidth="max-content"
                                    labelPosition="end"
                                    disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                    border="N"
                                    style={{ width: "max-content" }}
                                />
                            </div>
                        </div>
                        <div className="overlay-actions">
                            <CustomLoadingButton
                                onClick={handleReportSubmit}
                                borderRadiusType="both"
                                variant="tint"
                                color="#2563EB">
                                {currentAction === "addReport" ? t('add') : t('update')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </motion.div>)}

            {/* parameters */}
            {isParamDialogOpened && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">
                                {`${currentAction === "addParameter" ? t('addParameter') : t('updateParameter')}`}
                            </h1>
                            <CircularButton
                                currentTheme={currentTheme}
                                onClick={() => setIsParamDialogOpened(false)}
                                icon={<MdClose />}
                                width={"35px"}
                                height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            {currentAction === "editParameter" &&
                                <div className="input-item-with-label">
                                    <label
                                        htmlFor="param_order"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('order').toUpperCase()}
                                    </label>
                                    <input
                                        id="param_order"
                                        name="param_order"
                                        type="number"
                                        placeholder={t('order').toUpperCase()}
                                        className="form-input-item"
                                        style={{ width: "100px" }}
                                        value={selectedParameter?.param_order}
                                        onChange={(e) => handleParameterChange(e, selectedParameter?._id, 'param_order')}
                                    />
                                </div>}
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="param_label"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('label').toUpperCase()}
                                </label>
                                <input
                                    id="param_label"
                                    name="param_label"
                                    type="text"
                                    placeholder={t('label').toUpperCase()}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                    value={selectedParameter?.param_label}
                                    onChange={(e) => handleParameterChange(e, selectedParameter?._id, 'param_label')}
                                    onBlur={(e) => {
                                        if (e.target.value && !selectedParameter?.param_help_text) {
                                            handleParameterChange(e, selectedParameter?._id, 'param_help_text');
                                        }

                                        if (e.target.value && !selectedParameter?.param_placeholder) {
                                            handleParameterChange(e, selectedParameter?._id, 'param_placeholder');
                                        }
                                    }}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="param_name"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('name').toUpperCase()}
                                </label>
                                <input
                                    id="param_name"
                                    name="param_name"
                                    type="text"
                                    placeholder={t('name').toUpperCase()}
                                    className="form-input-item"
                                    style={{ width: "350px" }}
                                    value={selectedParameter?.param_name}
                                    onChange={(e) => handleParameterChange(e, selectedParameter?._id, 'param_name')}
                                    onBlur={(e) => {
                                        const formatted = formatParamName(e.target.value);
                                        handleParameterChange(
                                            { target: { value: formatted } },
                                            selectedParameter?._id,
                                            "param_name"
                                        );
                                    }}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="paramIcon"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('icon').toUpperCase()}
                                </label>
                                <Select
                                    id="paramIcon"
                                    name="paramIcon"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    options={qilloIconOptions}
                                    width={"350px"}
                                    isSearchable={true}
                                    placeholder={t('icon').toUpperCase()}
                                    value={selectedParamIcon}
                                    onChange={setSelectedParamIcon}
                                    getOptionLabel={(e) => (
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {e.icon} {e.label}
                                        </div>
                                    )}
                                />
                            </div>
                            <div className="input-item-with-label">
                                <label htmlFor="param_value_set_code"
                                    className="form-input-label"
                                    style={{ width: "100px" }}>
                                    {t('valueSet').toUpperCase()}
                                </label>
                                <Select
                                    id="param_value_set_code"
                                    name="param_value_set_code"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                        menuPortal: base => ({
                                            ...base,
                                            zIndex: 9999,
                                        })
                                    }}
                                    width="350px"
                                    value={valueSets.find(option => option.value === selectedParameter?.param_value_set_code) || null}
                                    onChange={(selectedOption) => handleParameterChange(selectedOption, selectedParameter?._id, 'param_value_set_code')}
                                    options={valueSets}
                                    isSearchable
                                    placeholder={t('valueSet').toUpperCase()}
                                />
                            </div>

                            <div className="input-item-with-label">
                                <label
                                    htmlFor="param_default_type"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('default').toUpperCase()}
                                </label>
                                <CustomRadioGroup
                                    id="param_default_type"
                                    value={selectedParameter?.param_default_type}
                                    onChange={(e) => handleParameterChange(e, selectedParameter?._id, 'param_default_type')}
                                    label={""}
                                    labelPosition="end"
                                    labelWidth="max-content"
                                    options={defaultTypes.map((v) => ({ value: v, label: v }))}
                                />
                            </div>
                            {renderDefaultValueInput()}
                            <div className="input-item-with-label">
                                <label
                                    htmlFor="param_help_text"
                                    className="form-dropdown-label"
                                    style={{ width: "100px" }}
                                >
                                    {t('help').toUpperCase()}
                                </label>
                                <textarea
                                    id="param_help_text"
                                    name="param_help_text"
                                    type="text"
                                    placeholder={t('help').toUpperCase()}
                                    rows={2}
                                    className="form-input-item"
                                    style={{ width: "350px", height: "max-content" }}
                                    value={selectedParameter?.param_help_text}
                                    onChange={(e) => handleParameterChange(e, selectedParameter?._id, 'param_help_text')}
                                />
                            </div>
                            <div className="form-grid-auto">
                                <div className="input-item-with-label">
                                    <label htmlFor="param_required"
                                        className="form-dropdown-label"
                                        style={{ width: "90px" }}
                                    />
                                    <ToggleSwitch
                                        id="param_required"
                                        value={selectedParameter?.param_required}
                                        onChange={(val) => handleParameterChange(val, selectedParameter?._id, 'param_required')}
                                        label={t('required').toUpperCase()}
                                        labelWidth="100px"
                                        labelPosition="end"
                                        disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                        border="N"
                                        style={{ width: "max-content" }}
                                    />
                                </div>
                                <div className="input-item-with-label">
                                    <label htmlFor="param_is_hidden"
                                        className="form-dropdown-label"
                                    />
                                    <ToggleSwitch
                                        id="param_is_hidden"
                                        value={selectedParameter?.param_is_hidden}
                                        onChange={(val) => handleParameterChange(val, selectedParameter?._id, 'param_is_hidden')}
                                        label={t('hidden').toUpperCase()}
                                        labelWidth="max-content"
                                        labelPosition="end"
                                        disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                        border="N"
                                        style={{ width: "max-content" }}
                                    />
                                </div>
                            </div>
                            <div className="form-grid-auto">
                                <div className="input-item-with-label">
                                    <label htmlFor="param_is_readonly"
                                        className="form-dropdown-label"
                                        style={{ width: "90px" }}
                                    />
                                    <ToggleSwitch
                                        id="param_is_readonly"
                                        value={selectedParameter?.param_is_readonly}
                                        onChange={(val) => handleParameterChange(val, selectedParameter?._id, 'param_is_readonly')}
                                        label={t('readOnly').toUpperCase()}
                                        labelWidth="100px"
                                        labelPosition="end"
                                        disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                        border="N"
                                        style={{ width: "max-content" }}
                                    />
                                </div>
                                <div className="input-item-with-label">
                                    <label htmlFor="param_trigger_refresh"
                                        className="form-dropdown-label"
                                    />
                                    <ToggleSwitch
                                        id="param_trigger_refresh"
                                        value={selectedParameter?.param_trigger_refresh}
                                        onChange={(val) => handleParameterChange(val, selectedParameter?._id, 'param_trigger_refresh')}
                                        label={t('triggerRefresh').toUpperCase()}
                                        labelWidth="max-content"
                                        labelPosition="end"
                                        disabled={!["admin", "superadmin"].includes(currentUser.user_type_xx)}
                                        border="N"
                                        style={{ width: "max-content" }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="overlay-actions">
                            <CustomLoadingButton
                                onClick={handleParameterSubmit}
                                borderRadiusType="both"
                                variant="tint"
                                color="#2563EB">
                                {currentAction === "addParameter" ? t('add') : t('update')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton currentTheme={currentTheme} variant="tint" color="#E040FB" onClick={() => setIsNotifyDialogOpen(false)}>{t('close')}</CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleConfirm}
                dialogTitle={t("confirmation")}
                dialogContent={<p dangerouslySetInnerHTML={{ __html: confirmationActiontext }} />}
            />
        </div>
    )
}



export default AllReportGroup;